/** HTML de impressão de pedido de exames — mesmo timbrado/rodapé dos demais documentos. */
import {
  CLINIC_DOC_SIGNATURE_CSS,
  buildClinicPrintDocumentHtml,
} from './clinicPrintLetterhead';
import { formatTaxIdDisplay, taxIdColonLabel } from './taxIdRegional';

const urgencyLabels: Record<string, string> = { routine: 'Rotina', urgent: 'Urgente', emergency: 'Emergência' };

function urgencyCellColor(urgency: string | undefined): string {
  if (urgency === 'urgent') return '#f59e0b';
  if (urgency === 'emergency') return '#ef4444';
  return 'var(--lh-accent)';
}

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hasText(v: unknown): boolean {
  return String(v ?? '').trim().length > 0;
}

function hasLaterality(item: any): boolean {
  return hasText(item.laterality) && item.laterality !== 'Não se aplica';
}

function formatRegionCell(item: any): string {
  const parts: string[] = [];
  if (hasText(item.body_region)) parts.push(esc(item.body_region));
  if (hasLaterality(item)) parts.push(`(${esc(item.laterality)})`);
  return parts.join(' ');
}

function formatDate(d: string | undefined | null): string {
  if (!d) return '-';
  const dt = new Date(String(d).split('T')[0] + 'T12:00:00');
  return dt.toLocaleDateString('pt-BR');
}

const EXAM_ORDER_EXTRA_CSS = `
  .exo-title { text-align: center; font-size: 15px; font-weight: 700; color: var(--lh-accent); margin: 4px 0 5px; text-transform: uppercase; letter-spacing: 1px; }
  .exo-number { text-align: center; font-size: 10px; color: #9ca3af; margin-bottom: 14px; }
  .exo-urgency-badge { text-align: center; margin-bottom: 10px; }
  .exo-urgency-badge span { display: inline-block; padding: 3px 14px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
  .info-item { font-size: 11px; line-height: 1.4; }
  .info-label { font-weight: 600; color: #374151; }
  .info-value { color: #4b5563; }
  table.exo-table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  table.exo-table th { background: rgba(var(--lh-accent-rgb), 0.08); border: 1px solid rgba(var(--lh-accent-rgb), 0.22); padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 700; color: var(--lh-accent); }
  table.exo-table td { border: 1px solid #e5e7eb; padding: 6px 8px; font-size: 11px; }
  .exo-exam-name { font-weight: 600; }
  .exo-exam-prep { font-size: 9px; color: #6b7280; margin-top: 2px; font-style: italic; }
  .exo-prep-box { background: #fefce8; border: 1px solid #fde68a; border-radius: 6px; padding: 10px; margin-top: 12px; }
  .exo-prep-box h4 { margin: 0 0 6px; font-size: 12px; color: #b45309; }
  .exo-prep-item { font-size: 10px; margin-bottom: 4px; color: #78350f; }
  .exo-destination-box { background: rgba(var(--lh-accent-rgb), 0.04); border: 1px solid rgba(var(--lh-accent-rgb), 0.18); border-left: 3px solid var(--lh-accent); border-radius: 6px; padding: 10px; margin-top: 8px; }
  .exo-validity { text-align: left; margin-top: 24px; margin-bottom: 0; font-size: 10px; color: #6b7280; }
  .exo-footer-note { text-align: center; margin-top: 8px; font-size: 9px; color: #9ca3af; }
  ${CLINIC_DOC_SIGNATURE_CSS}
`;

export function buildExamOrderPrintHtml(printData: { order: any; clinic: any }): string {
  const safe = printData || { order: {}, clinic: {} };
  const order = safe.order || {};
  const clinic = safe.clinic || {};
  const items = Array.isArray(order.items) ? order.items : [];

  const showCode = items.some((i: any) => hasText(i.exam_code));
  const showMaterial = items.some((i: any) => hasText(i.specimen_type));
  const showRegion = items.some((i: any) => hasText(i.body_region) || hasLaterality(i));

  const tableHeaders = [
    '<th style="width:5%;">#</th>',
    ...(showCode ? ['<th style="width:10%;">Código</th>'] : []),
    '<th>Exame</th>',
    ...(showMaterial ? ['<th style="width:12%;">Material</th>'] : []),
    ...(showRegion ? ['<th style="width:12%;">Região</th>'] : []),
    '<th style="width:8%;">Urgência</th>',
  ];
  const colCount = tableHeaders.length;

  const genderLabel =
    order.patient_gender === 'masculine'
      ? 'Masculino'
      : order.patient_gender === 'feminine'
        ? 'Feminino'
        : order.patient_gender || '';

  const rows = items
    .map((item: any, idx: number) => {
      const cells = [
        `<td>${idx + 1}</td>`,
        ...(showCode ? [`<td>${hasText(item.exam_code) ? esc(item.exam_code) : ''}</td>`] : []),
        `<td><span class="exo-exam-name">${esc(item.exam_name)}</span>${item.clinical_justification ? `<div class="exo-exam-prep">Justificativa: ${esc(item.clinical_justification)}</div>` : ''}</td>`,
        ...(showMaterial ? [`<td>${hasText(item.specimen_type) ? esc(item.specimen_type) : ''}</td>`] : []),
        ...(showRegion ? [`<td>${formatRegionCell(item)}</td>`] : []),
        `<td style="color:${urgencyCellColor(item.urgency)};font-weight:600;font-size:10px;">${esc(urgencyLabels[item.urgency] || 'Rotina')}</td>`,
      ];
      return `<tr>${cells.join('')}</tr>`;
    })
    .join('');

  const prepItems = items.filter((i: any) => i.preparation_instructions);

  const sigImgUrl = String(order.professional_signature_image_url ?? '').trim();
  const sigImg = sigImgUrl
    ? `<div class="clinic-doc-sign-prof-img-wrap"><img src="${esc(sigImgUrl)}" alt="" /></div>`
    : '';
  const spacerCls = sigImg ? 'clinic-doc-sign-spacer clinic-doc-sign-spacer--with-img' : 'clinic-doc-sign-spacer';
  const credentialsHtml = Array.isArray(order.professional_credentials)
    ? order.professional_credentials.filter(Boolean).map((c: string) => `<p class="clinic-doc-sign-caption" style="font-style:italic;color:#374151;">${esc(c)}</p>`).join('')
    : '';
  const regLine =
    order.professional_registration || clinic.responsible_registration
      ? `<p class="clinic-doc-sign-caption">${esc(order.professional_registration_type || clinic.responsible_registration_type || 'CRM')} ${esc(order.professional_registration || clinic.responsible_registration)}${order.professional_registration_state ? ` - ${esc(order.professional_registration_state)}` : ''}</p>`
      : '';

  const bodyHtml = `
<style>${EXAM_ORDER_EXTRA_CSS}</style>
<div class="exo-title">SOLICITAÇÃO DE EXAMES</div>
${order.urgency && order.urgency !== 'routine' ? `<div class="exo-urgency-badge"><span style="background-color:${order.urgency === 'emergency' ? '#fef2f2' : '#fefce8'};color:${order.urgency === 'emergency' ? '#ef4444' : '#f59e0b'};">${esc(urgencyLabels[order.urgency] || 'Rotina')}</span></div>` : ''}
<div class="exo-number">Nº ${esc(order.order_number)} | Emissão: ${formatDate(order.issue_date)} | Validade: ${formatDate(order.validity_date)}</div>
<div class="section">
  <div class="section-title">Dados do Paciente</div>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Nome: </span><span class="info-value">${esc(order.patient_name)}</span></div>
    ${order.patient_cpf ? `<div class="info-item"><span class="info-label">${taxIdColonLabel()} </span><span class="info-value">${esc(formatTaxIdDisplay(order.patient_cpf))}</span></div>` : ''}
    ${order.patient_age_text ? `<div class="info-item"><span class="info-label">Idade: </span><span class="info-value">${esc(order.patient_age_text)}</span></div>` : ''}
    ${genderLabel ? `<div class="info-item"><span class="info-label">Sexo: </span><span class="info-value">${esc(genderLabel)}</span></div>` : ''}
    ${order.patient_address ? `<div class="info-item"><span class="info-label">Endereço: </span><span class="info-value">${esc(order.patient_address)}</span></div>` : ''}
  </div>
</div>
${order.clinical_indication || order.diagnosis || order.clinical_history || order.current_medications || order.allergies ? `<div class="section">
  <div class="section-title">Informações Clínicas</div>
  ${order.clinical_indication ? `<div class="info-item" style="margin-bottom:4px;"><span class="info-label">Indicação Clínica: </span><span class="info-value">${esc(order.clinical_indication)}</span></div>` : ''}
  ${order.diagnosis ? `<div class="info-item" style="margin-bottom:4px;"><span class="info-label">Diagnóstico: </span><span class="info-value">${esc(order.diagnosis)}${order.diagnosis_code ? ` (CID: ${esc(order.diagnosis_code)})` : ''}</span></div>` : ''}
  ${order.clinical_history ? `<div class="info-item" style="margin-bottom:4px;"><span class="info-label">História Clínica: </span><span class="info-value">${esc(order.clinical_history)}</span></div>` : ''}
  ${order.current_medications ? `<div class="info-item" style="margin-bottom:4px;"><span class="info-label">Medicamentos em Uso: </span><span class="info-value">${esc(order.current_medications)}</span></div>` : ''}
  ${order.allergies ? `<div class="info-item"><span class="info-label">Alergias: </span><span class="info-value">${esc(order.allergies)}</span></div>` : ''}
</div>` : ''}
<div class="section">
  <div class="section-title">Exames Solicitados</div>
  <table class="exo-table">
    <thead><tr>${tableHeaders.join('')}</tr></thead>
    <tbody>${rows || `<tr><td colspan="${colCount}" style="text-align:center;color:#9ca3af;">(sem exames)</td></tr>`}</tbody>
  </table>
</div>
${prepItems.length ? `<div class="exo-prep-box">
  <h4>Orientações de Preparo ao Paciente</h4>
  ${prepItems.map((item: any) => `<div class="exo-prep-item"><strong>${esc(item.exam_name)}:</strong> ${esc(item.preparation_instructions)}</div>`).join('')}
</div>` : ''}
${order.special_instructions ? `<div class="exo-prep-box" style="margin-top:8px;"><h4>Instruções Especiais</h4><div class="exo-prep-item">${esc(order.special_instructions)}</div></div>` : ''}
${order.destination_lab ? `<div class="exo-destination-box">
  <div style="font-weight:600;font-size:11px;margin-bottom:4px;">Laboratório / Clínica de Destino</div>
  <div style="font-size:10px;">${esc(order.destination_lab)}</div>
  ${order.destination_phone ? `<div style="font-size:10px;">Tel: ${esc(order.destination_phone)}</div>` : ''}
  ${order.destination_address ? `<div style="font-size:10px;">${esc(order.destination_address)}</div>` : ''}
</div>` : ''}
${order.notes ? `<div class="section" style="margin-top:12px;"><div class="section-title">Observações</div><p style="font-size:11px;line-height:1.6;">${esc(order.notes)}</p></div>` : ''}
<div class="exo-validity">Pedido válido até ${formatDate(order.validity_date)}</div>
<div class="clinic-doc-sign-zone">
  <div class="clinic-doc-sign-row">
    <div class="clinic-doc-sign-slot">
      ${sigImg}
      <div class="${spacerCls}"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">${esc(order.professional_name || clinic.responsible_name || 'Profissional Responsável')}</p>
      ${credentialsHtml}
      ${regLine}
    </div>
    <div class="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
      <div class="clinic-doc-sign-spacer"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">Data: ${formatDate(order.issue_date)}</p>
    </div>
  </div>
</div>
${clinic.exam_order_footer_text ? `<div class="exo-footer-note">${esc(clinic.exam_order_footer_text)}</div>` : ''}`;

  return buildClinicPrintDocumentHtml({
    clinic,
    browserTitle: 'Pedido de Exames',
    bodyHtml,
  });
}
